import { useEffect, useMemo, useState } from "react";
import {
  Users as UsersIcon,
  Shield,
  Briefcase,
  BriefcaseBusiness,
  UserCheck,
  Search,
  Plus,
  Edit,
  UserX,
  Mail,
  X,
  LoaderCircle,
} from "lucide-react";
import {
  PageHeader,
  Card,
  StatCard,
  Badge,
  useToast,
  formatDate,
} from "../adminComponents/adminComponents";
import { ROLES, ROLE_LABELS } from "../../constants/roles";
import { useAuth } from "../../context/authContext";
import {
  createUser,
  activateUser,
  deactivateUser,
  updateUser,
} from "../../api";
import { useUserManagementHook } from "../../hooks/useUserManagementHook";
import { useAdminVenturesHook } from "../../hooks/useAdminHooks";
import "./users.css";

const ROLE_BADGE_TONES = {
  [ROLES.ADMIN]: "danger",
  [ROLES.MANAGER]: "primary",
  [ROLES.FUNDER]: "accent",
  [ROLES.VENTURE]: "success",
};

function normalizeUserRole(role) {
  const normalized = String(role ?? "")
    .trim()
    .toUpperCase();

  switch (normalized) {
    case "ADMIN":
      return ROLES.ADMIN;
    case "MANAGER":
    case "PROGRAM_MANAGER":
      return ROLES.MANAGER;
    case "FUNDER":
      return ROLES.FUNDER;
    case "VENTURE":
      return ROLES.VENTURE;
    default:
      return String(role ?? "").toLowerCase();
  }
}

function formatLastLogin(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.floor((now - date) / 86400000);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;
  return formatDate(dateStr);
}

function getVentureName(ventureId) {
  return ventureId || "—";
}

function parseLinkedVentureIds(linkedVenture) {
  if (!linkedVenture) return [];

  try {
    const parsed = JSON.parse(String(linkedVenture));
    if (Array.isArray(parsed)) {
      return parsed
        .map((ventureId) => String(ventureId).trim())
        .filter(Boolean);
    }
  } catch {
    // fall through to legacy CSV parsing
  }

  return String(linkedVenture)
    .split(",")
    .map((ventureId) => ventureId.trim())
    .filter(Boolean);
}

const EMPTY_FORM = {
  name: "",
  email: "",
  role: ROLES.VENTURE,
  phone: "",
  ventureId: "",
};

export default function Users() {
  const { token, user } = useAuth();
  const { toast, ToastViewport } = useToast();
  const [users, setUsers] = useState([]);
  const {
    data: fetchedUsers = [],
    isLoading,
    isError,
    error,
  } = useUserManagementHook({ token, enabled: !!token });
  const { data: ventures = [] } = useAdminVenturesHook({
    token,
    userId: user?.id,
    enabled: !!token,
  });
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("active");
  const [addOpen, setAddOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [pendingStatusUser, setPendingStatusUser] = useState(null);
  const [confirmEmailInput, setConfirmEmailInput] = useState("");
  const [linkVenturesUser, setLinkVenturesUser] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [isSavingVentureLink, setIsSavingVentureLink] = useState(false);

  useEffect(() => {
    if (!Array.isArray(fetchedUsers) || fetchedUsers.length === 0) {
      return;
    }

    const normalizedUsers = fetchedUsers.map((user) => ({
      ...user,
      role: normalizeUserRole(user.role),
      status: user.status?.toLowerCase() || "active",
      lastLogin: user.lastLogin || user.updatedAt || user.createdAt,
      createdAt: user.createdAt || new Date().toISOString(),
      ventureIds: parseLinkedVentureIds(user.linked_venture),
      ventureId:
        user.ventureId ||
        parseLinkedVentureIds(user.linked_venture)[0] ||
        user.linked_venture ||
        "",
    }));

    setUsers(normalizedUsers);
  }, [fetchedUsers]);

  const venturesById = useMemo(() => {
    return ventures.reduce((acc, venture) => {
      acc[String(venture.id)] = venture;
      return acc;
    }, {});
  }, [ventures]);

  function getVentureNameById(ventureId) {
    if (!ventureId) return "—";
    return venturesById[String(ventureId)]?.name ?? getVentureName(ventureId);
  }

  function getLinkedVentureIdsForSearch(candidateUser) {
    if (
      Array.isArray(candidateUser.ventureIds) &&
      candidateUser.ventureIds.length
    ) {
      return candidateUser.ventureIds;
    }
    return candidateUser.ventureId ? [candidateUser.ventureId] : [];
  }

  function userMatchesQuery(candidateUser, query) {
    if (!query) return true;
    const q = query.trim().toLowerCase();
    if (!q) return true;

    const includes = (value) =>
      String(value ?? "")
        .toLowerCase()
        .includes(q);

    if (
      includes(candidateUser.name) ||
      includes(candidateUser.email) ||
      includes(candidateUser.id) ||
      includes(candidateUser.phone)
    ) {
      return true;
    }

    const ventureIds = getLinkedVentureIdsForSearch(candidateUser);
    for (const ventureId of ventureIds) {
      if (includes(ventureId)) return true;
      const venture = venturesById[String(ventureId)];
      if (!venture) continue;
      if (
        includes(venture.id) ||
        includes(venture.name) ||
        includes(venture.geography) ||
        includes(venture?.founder?.name) ||
        includes(venture?.founder_name) ||
        includes(venture?.founderName) ||
        includes(venture?.founder?.email)
      ) {
        return true;
      }
    }

    return false;
  }

  const filtered = users.filter((candidateUser) => {
    const matchesSearch = userMatchesQuery(candidateUser, search);
    const matchesRole =
      roleFilter === "all" || candidateUser.role === roleFilter;
    const matchesStatus =
      statusFilter === "all" || candidateUser.status === statusFilter;
    return matchesSearch && matchesRole && matchesStatus;
  });

  const totalUsers = users.length;
  const adminCount = users.filter((user) => user.role === ROLES.ADMIN).length;
  const managerCount = users.filter(
    (user) => user.role === ROLES.MANAGER,
  ).length;
  const externalCount = users.filter(
    (user) => user.role === ROLES.FUNDER || user.role === ROLES.VENTURE,
  ).length;

  function getLinkedVentureDisplay(user) {
    if (user.role === ROLES.VENTURE) {
      return getVentureNameById(user.ventureId);
    }

    if (user.role === ROLES.FUNDER) {
      const count = user.ventureIds?.length ?? 0;
      if (count === 0) return "No ventures assigned";
      return `${count} venture${count === 1 ? "" : "s"} assigned`;
    }

    return "—";
  }

  function openAdd() {
    setForm(EMPTY_FORM);
    setAddOpen(true);
  }

  function openEdit(user) {
    setEditUser(user);
    setForm({
      name: user.name,
      email: user.email,
      role: user.role,
      phone: user.phone || "",
      ventureId: user.ventureId || "",
    });
  }

  function openManageVentures(userToLink) {
    setLinkVenturesUser(userToLink);
  }

  async function handleSaveVentureLink({ selectedIds }) {
    if (!token || !linkVenturesUser?.id) {
      toast({
        title: "Session expired",
        description: "Please sign in again before assigning ventures.",
        tone: "danger",
      });
      return;
    }

    const isVenture = linkVenturesUser.role === ROLES.VENTURE;

    setIsSavingVentureLink(true);
    try {
      const linkPayload = isVenture
        ? { linkedVenture: selectedIds[0] || "" }
        : { linkedVentures: selectedIds };

      const result = await updateUser({
        token,
        userId: linkVenturesUser.id,
        name: linkVenturesUser.name,
        email: linkVenturesUser.email,
        role: linkVenturesUser.role,
        phone: linkVenturesUser.phone,
        ...linkPayload,
      });

      if (!result.ok) {
        toast({
          title: "Assignment failed",
          description: result.error,
          tone: "danger",
        });
        return;
      }

      setUsers((prev) =>
        prev.map((existingUser) => {
          if (existingUser.id !== linkVenturesUser.id) return existingUser;
          if (isVenture) {
            const nextId = selectedIds[0] || "";
            return {
              ...existingUser,
              ventureId: nextId,
              ventureIds: nextId ? [nextId] : [],
              linked_venture: nextId,
            };
          }
          return {
            ...existingUser,
            ventureIds: [...selectedIds],
            ventureId: selectedIds[0] ?? "",
            linked_venture: JSON.stringify(selectedIds),
          };
        }),
      );

      toast({
        title: isVenture ? "Venture linked" : "Ventures updated",
        description: isVenture
          ? selectedIds[0]
            ? `${linkVenturesUser.name} is now linked to the selected venture.`
            : `${linkVenturesUser.name} is no longer linked to a venture.`
          : `${linkVenturesUser.name} now has access to ${selectedIds.length} venture${selectedIds.length === 1 ? "" : "s"}.`,
      });
      setLinkVenturesUser(null);
    } finally {
      setIsSavingVentureLink(false);
    }
  }

  async function handleAddUser() {
    if (isAddingUser) return;

    if (!token) {
      toast({
        title: "Session expired",
        description: "Please sign in again before creating users.",
        tone: "danger",
      });
      return;
    }

    setIsAddingUser(true);

    try {
      const result = await createUser({
        token,
        name: form.name,
        email: form.email,
        role: form.role,
        phone: form.phone,
        linkedVenture: form.ventureId,
      });

      if (!result.ok) {
        toast({
          title: "Create user failed",
          description: result.error,
          tone: "danger",
        });
        return;
      }

      const newUser = {
        id: result.user?.id ?? String(Date.now()),
        name: result.user?.name ?? form.name,
        email: result.user?.email ?? form.email,
        role: form.role,
        phone: form.phone,
        ventureId: form.ventureId || "",
        status: "active",
        lastLogin: new Date().toISOString(),
        createdAt: result.user?.createdAt ?? new Date().toISOString(),
      };

      setUsers((prev) => [...prev, newUser]);
      toast({
        title: "User created",
        description: result.inviteEmailSent
          ? `${form.name} has been added as ${ROLE_LABELS[form.role]}. A password setup email has been sent.`
          : `${form.name} has been added as ${ROLE_LABELS[form.role]}. The password setup email could not be sent automatically.`,
      });
      setAddOpen(false);
    } finally {
      setIsAddingUser(false);
    }
  }

  async function handleEditUser() {
    if (!token || !editUser?.id) {
      toast({
        title: "Session expired",
        description: "Please sign in again before editing users.",
        tone: "danger",
      });
      return;
    }

    const result = await updateUser({
      token,
      userId: editUser.id,
      name: form.name,
      email: form.email,
      role: form.role,
      phone: form.phone,
      linkedVenture: form.ventureId,
    });

    if (!result.ok) {
      toast({
        title: "Update failed",
        description: result.error,
        tone: "danger",
      });
      return;
    }

    setUsers((prev) =>
      prev.map((user) =>
        user.id === editUser.id
          ? {
              ...user,
              name: result.user?.name ?? form.name,
              email: result.user?.email ?? form.email,
              role: normalizeUserRole(result.user?.role ?? form.role),
              phone: result.user?.phone ?? form.phone,
              ventureId:
                result.user?.linked_venture ??
                result.user?.ventureId ??
                form.ventureId,
              updatedAt: result.user?.updatedAt ?? user.updatedAt,
            }
          : user,
      ),
    );
    toast({
      title: "User Updated",
      description: `${form.name}'s profile has been updated.`,
    });
    setEditUser(null);
  }

  async function handleToggleStatus(targetUser) {
    const isActive = targetUser.status === "active";
    const next = isActive ? "inactive" : "active";

    const result = isActive
      ? await deactivateUser({ token, userId: targetUser.id })
      : await activateUser({ token, userId: targetUser.id });

    if (!result.ok) {
      toast({
        title: isActive ? "Deactivation failed" : "Activation failed",
        description: result.error,
        tone: "danger",
      });
      return false;
    }

    setUsers((prev) =>
      prev.map((user) =>
        user.id === targetUser.id ? { ...user, status: next } : user,
      ),
    );
    toast({
      title: next === "active" ? "User Activated" : "User Deactivated",
      description: `${targetUser.name}'s account has been ${next === "active" ? "activated" : "deactivated"}.`,
    });

    return true;
  }

  function requestStatusToggle(targetUser) {
    setPendingStatusUser(targetUser);
    setConfirmEmailInput("");
  }

  function closeStatusConfirmation() {
    setPendingStatusUser(null);
    setConfirmEmailInput("");
  }

  async function confirmStatusChange() {
    if (!pendingStatusUser) {
      return;
    }

    const isDeactivation = pendingStatusUser.status === "active";
    if (
      isDeactivation &&
      confirmEmailInput.trim().toLowerCase() !==
        String(pendingStatusUser.email ?? "")
          .trim()
          .toLowerCase()
    ) {
      toast({
        title: "Email confirmation required",
        description: "Type the exact user email to confirm deactivation.",
        tone: "danger",
      });
      return;
    }

    const didUpdate = await handleToggleStatus(pendingStatusUser);
    if (didUpdate) {
      closeStatusConfirmation();
    }
  }

  return (
    <div className="adminUsers">
      <PageHeader
        title="User Management"
        description="Manage platform users, roles, and access permissions."
        actions={
          <button
            type="button"
            className="admin-btn admin-btn-primary"
            onClick={openAdd}
          >
            <Plus className="admin-btn-icon" />
            Add User
          </button>
        }
      />

      <div className="admin-grid admin-grid-4">
        <StatCard
          icon={UsersIcon}
          title="Total Users"
          value={totalUsers}
          description="All platform users"
        />
        <StatCard
          icon={Shield}
          title="Administrators"
          value={adminCount}
          description="Full system access"
        />
        <StatCard
          icon={Briefcase}
          title="Program Managers"
          value={managerCount}
          description="Partial System Access"
        />
        <StatCard
          icon={UserCheck}
          title="External Users"
          value={externalCount}
          description="Funders & ventures"
        />
      </div>

      <Card
        title="All Users"
        actions={
          <div className="admin-row">
            <div className="admin-input-wrap">
              <Search className="admin-input-icon" />
              <input
                className="admin-input admin-input-search"
                placeholder="Search by user, venture, founder, location, or ID…"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
            <select
              className="admin-select"
              value={roleFilter}
              onChange={(event) => setRoleFilter(event.target.value)}
            >
              <option value="all">All Roles</option>
              {Object.entries(ROLE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <select
              className="admin-select"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        }
      >
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Linked Venture</th>
                <th>Status</th>
                <th>Last Login</th>
                <th>Created</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={8} className="admin-muted">
                    Loading users...
                  </td>
                </tr>
              )}
              {isError && (
                <tr>
                  <td colSpan={8} className="admin-muted">
                    {error?.message || "Unable to load users."}
                  </td>
                </tr>
              )}
              {!isLoading && !isError && filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="admin-muted">
                    No users found.
                  </td>
                </tr>
              )}
              {!isLoading &&
                !isError &&
                filtered.map((user) => (
                  <tr key={user.id}>
                    <td style={{ fontWeight: 600 }}>{user.name}</td>
                    <td>
                      <span className="adminUsers-email">
                        <Mail size={13} />
                        {user.email}
                      </span>
                    </td>
                    <td>
                      <Badge tone={ROLE_BADGE_TONES[user.role]}>
                        {ROLE_LABELS[user.role]}
                      </Badge>
                    </td>
                    <td>{getLinkedVentureDisplay(user)}</td>
                    <td>
                      <Badge
                        tone={user.status === "active" ? "success" : "muted"}
                      >
                        {user.status === "active" ? "Active" : "Inactive"}
                      </Badge>
                    </td>
                    <td className="admin-muted">
                      {formatLastLogin(user.lastLogin)}
                    </td>
                    <td className="admin-muted">
                      {formatDate(user.createdAt)}
                    </td>
                    <td>
                      <div className="adminUsers-actions">
                        {(user.role === ROLES.FUNDER ||
                          user.role === ROLES.VENTURE) && (
                          <button
                            type="button"
                            className="admin-btn admin-btn-ghost admin-btn-sm"
                            title={
                              user.role === ROLES.FUNDER
                                ? "Manage linked ventures"
                                : "Link venture"
                            }
                            onClick={() => openManageVentures(user)}
                          >
                            <BriefcaseBusiness size={14} />
                          </button>
                        )}
                        <button
                          type="button"
                          className="admin-btn admin-btn-ghost admin-btn-sm"
                          title="Edit"
                          onClick={() => openEdit(user)}
                        >
                          <Edit size={14} />
                        </button>
                        <button
                          type="button"
                          className={
                            "admin-btn admin-btn-sm " +
                            (user.status === "active"
                              ? "admin-btn-ghost"
                              : "admin-btn-outline")
                          }
                          title={
                            user.status === "active" ? "Deactivate" : "Activate"
                          }
                          onClick={() => requestStatusToggle(user)}
                        >
                          <UserX size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </Card>

      {addOpen && (
        <UserDialog
          title="Add New User"
          description="Create a new user account with role-based access."
          form={form}
          setForm={setForm}
          onConfirm={handleAddUser}
          onClose={() => setAddOpen(false)}
          confirmLabel="Create User"
          isSubmitting={isAddingUser}
          ventures={ventures}
        />
      )}

      {editUser && (
        <UserDialog
          title="Edit User"
          description="Update user profile and access settings."
          form={form}
          setForm={setForm}
          onConfirm={handleEditUser}
          onClose={() => setEditUser(null)}
          confirmLabel="Save Changes"
          ventures={ventures}
        />
      )}

      {linkVenturesUser && (
        <LinkVenturesDialog
          user={linkVenturesUser}
          ventures={ventures}
          mode={linkVenturesUser.role === ROLES.VENTURE ? "single" : "multiple"}
          initialSelectedIds={linkVenturesUser.ventureIds ?? []}
          onClose={() => setLinkVenturesUser(null)}
          onSave={handleSaveVentureLink}
          isSubmitting={isSavingVentureLink}
        />
      )}

      {pendingStatusUser && (
        <ConfirmStatusChangeDialog
          user={pendingStatusUser}
          confirmEmailInput={confirmEmailInput}
          setConfirmEmailInput={setConfirmEmailInput}
          onClose={closeStatusConfirmation}
          onConfirm={confirmStatusChange}
        />
      )}

      <ToastViewport />
    </div>
  );
}

function ConfirmStatusChangeDialog({
  user,
  confirmEmailInput,
  setConfirmEmailInput,
  onClose,
  onConfirm,
}) {
  const isDeactivation = user.status === "active";

  return (
    <div className="adminUsers-overlay" role="dialog" aria-modal="true">
      <div className="adminUsers-dialog">
        <header className="adminUsers-dialog-header">
          <div>
            <h2 className="adminUsers-dialog-title">
              {isDeactivation ? "Deactivate User?" : "Activate User?"}
            </h2>
            <p className="adminUsers-dialog-desc">
              {isDeactivation
                ? `${user.name} will no longer be able to access the platform until reactivated.`
                : `${user.name} will regain access to the platform immediately.`}
            </p>
          </div>
          <button
            type="button"
            className="admin-btn admin-btn-ghost admin-btn-sm"
            onClick={onClose}
          >
            <X size={16} />
          </button>
        </header>

        {isDeactivation && (
          <div className="adminUsers-dialog-body">
            <div className="adminUsers-field">
              <label className="admin-label">
                Type <strong>{user.email}</strong> to confirm
              </label>
              <input
                type="email"
                className="admin-input"
                style={{ width: "100%" }}
                value={confirmEmailInput}
                onChange={(event) => setConfirmEmailInput(event.target.value)}
                placeholder="Enter user email"
                autoComplete="off"
              />
            </div>
          </div>
        )}

        <footer className="adminUsers-dialog-footer">
          <button
            type="button"
            className="admin-btn admin-btn-outline"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="admin-btn admin-btn-primary"
            onClick={onConfirm}
            disabled={
              isDeactivation &&
              confirmEmailInput.trim().toLowerCase() !==
                String(user.email ?? "")
                  .trim()
                  .toLowerCase()
            }
          >
            {isDeactivation ? "Confirm Deactivation" : "Confirm Activation"}
          </button>
        </footer>
      </div>
    </div>
  );
}

function UserDialog({
  title,
  description,
  form,
  setForm,
  onConfirm,
  onClose,
  confirmLabel,
  isSubmitting = false,
  ventures = [],
}) {
  const set = (key) => (event) =>
    setForm((prev) => ({ ...prev, [key]: event.target.value }));

  return (
    <div className="adminUsers-overlay" role="dialog" aria-modal="true">
      <div className="adminUsers-dialog">
        <header className="adminUsers-dialog-header">
          <div>
            <h2 className="adminUsers-dialog-title">{title}</h2>
            {description && (
              <p className="adminUsers-dialog-desc">{description}</p>
            )}
          </div>
          <button
            type="button"
            className="admin-btn admin-btn-ghost admin-btn-sm"
            onClick={onClose}
            disabled={isSubmitting}
          >
            <X size={16} />
          </button>
        </header>

        <div className="adminUsers-dialog-body">
          <div className="adminUsers-field">
            <label className="admin-label">Full Name</label>
            <input
              className="admin-input"
              style={{ width: "100%" }}
              value={form.name}
              onChange={set("name")}
              placeholder="Enter full name"
              disabled={isSubmitting}
            />
          </div>
          <div className="adminUsers-field">
            <label className="admin-label">Email</label>
            <input
              className="admin-input"
              style={{ width: "100%" }}
              type="email"
              value={form.email}
              onChange={set("email")}
              placeholder="Enter email address"
              disabled={isSubmitting}
            />
          </div>
          <div className="adminUsers-field">
            <label className="admin-label">Phone Number</label>
            <input
              className="admin-input"
              style={{ width: "100%" }}
              value={form.phone}
              onChange={set("phone")}
              placeholder="+256 7XX XXX XXX"
              disabled={isSubmitting}
            />
          </div>
          <div className="adminUsers-field">
            <label className="admin-label">Role</label>
            <select
              className="admin-select"
              style={{ width: "100%" }}
              value={form.role}
              onChange={set("role")}
              disabled={isSubmitting}
            >
              {Object.entries(ROLE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          {form.role === ROLES.VENTURE && (
            <div className="adminUsers-field">
              <label className="admin-label">Linked Venture</label>
              <select
                className="admin-select"
                style={{ width: "100%" }}
                value={form.ventureId}
                onChange={set("ventureId")}
                disabled={isSubmitting}
              >
                <option value="">— Select venture —</option>
                {ventures.map((venture) => (
                  <option key={venture.id} value={venture.id}>
                    {venture.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <footer className="adminUsers-dialog-footer">
          <button
            type="button"
            className="admin-btn admin-btn-outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="admin-btn admin-btn-primary"
            onClick={onConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <LoaderCircle size={15} className="adminUsers-spin" />
                Creating...
              </>
            ) : (
              confirmLabel
            )}
          </button>
        </footer>
      </div>
    </div>
  );
}

function LinkVenturesDialog({
  user,
  ventures,
  mode,
  initialSelectedIds,
  onClose,
  onSave,
  isSubmitting,
}) {
  const isMultiple = mode === "multiple";
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState(() =>
    Array.isArray(initialSelectedIds)
      ? initialSelectedIds.map((id) => String(id))
      : [],
  );

  const filteredVentures = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return ventures;

    // Rank: name-prefix > name-contains > founder > geography > id
    return ventures
      .map((venture) => {
        const ventureName = String(venture?.name ?? "").toLowerCase();
        const founderName = String(
          venture?.founder?.name ??
            venture?.founder_name ??
            venture?.founderName ??
            "",
        ).toLowerCase();
        const geography = String(venture?.geography ?? "").toLowerCase();
        const ventureId = String(venture?.id ?? "").toLowerCase();

        let score = -1;
        if (ventureName.startsWith(query)) score = 0;
        else if (ventureName.includes(query)) score = 1;
        else if (founderName.includes(query)) score = 2;
        else if (geography.includes(query)) score = 3;
        else if (ventureId.includes(query)) score = 4;

        return { venture, score };
      })
      .filter((item) => item.score !== -1)
      .sort((a, b) => a.score - b.score)
      .map((item) => item.venture);
  }, [ventures, search]);

  function toggleId(id) {
    if (isMultiple) {
      setSelectedIds((current) =>
        current.includes(id)
          ? current.filter((existing) => existing !== id)
          : [...current, id],
      );
    } else {
      setSelectedIds((current) => (current[0] === id ? [] : [id]));
    }
  }

  const selectionSummary = isMultiple
    ? `${selectedIds.length} selected`
    : selectedIds[0]
      ? "1 venture selected"
      : "None selected";

  return (
    <div className="adminUsers-overlay" role="dialog" aria-modal="true">
      <div className="adminUsers-dialog adminUsers-dialog-wide">
        <header className="adminUsers-dialog-header">
          <div>
            <h2 className="adminUsers-dialog-title">
              {isMultiple ? "Manage Ventures" : "Link Venture"}
            </h2>
            <p className="adminUsers-dialog-desc">
              {isMultiple
                ? `Assign one or more ventures to ${user.name}.`
                : `Link ${user.name} to a venture. Only one venture can be linked to a venture account.`}
            </p>
          </div>
          <button
            type="button"
            className="admin-btn admin-btn-ghost admin-btn-sm"
            onClick={onClose}
            disabled={isSubmitting}
          >
            <X size={16} />
          </button>
        </header>

        <div className="adminUsers-dialog-body">
          <div className="adminUsers-field">
            <label className="admin-label">Search ventures</label>
            <div className="admin-input-wrap">
              <Search className="admin-input-icon" />
              <input
                className="admin-input admin-input-search"
                placeholder="Search ventures, founders, or locations"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className="adminUsers-ventureMeta">
            <Badge tone="outline">{selectionSummary}</Badge>
          </div>

          <div className="adminUsers-ventureList">
            {filteredVentures.map((venture, index) => {
              const ventureId = String(venture.id ?? "");
              const rowKey = `${ventureId || "venture"}-${index}`;
              const isChecked = selectedIds.includes(ventureId);
              const founderName =
                venture?.founder?.name ?? venture?.founder_name;
              return (
                <label key={rowKey} className="adminUsers-ventureItem">
                  <input
                    type={isMultiple ? "checkbox" : "radio"}
                    name={isMultiple ? undefined : "linked-venture"}
                    checked={isChecked}
                    onChange={() => toggleId(ventureId)}
                    disabled={isSubmitting}
                  />
                  <div className="adminUsers-ventureInfo">
                    <p className="adminUsers-ventureName">{venture.name}</p>
                    <p className="adminUsers-ventureDetails">
                      {founderName || "Unknown founder"}
                      {venture.geography ? ` | ${venture.geography}` : ""}
                    </p>
                  </div>
                </label>
              );
            })}
            {filteredVentures.length === 0 && (
              <p className="admin-muted">No ventures found.</p>
            )}
          </div>
        </div>

        <footer className="adminUsers-dialog-footer">
          <button
            type="button"
            className="admin-btn admin-btn-outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="admin-btn admin-btn-primary"
            onClick={() => onSave({ selectedIds })}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? "Saving..."
              : isMultiple
                ? "Save Assignments"
                : "Save"}
          </button>
        </footer>
      </div>
    </div>
  );
}
